// Per-language allowed-character sets for the Custom Vocabulary "Term" field.
// Mirrors AWS Transcribe's custom-vocabulary character sets
// (https://docs.aws.amazon.com/transcribe/latest/dg/charsets.html), keyed by base
// language code (e.g. "es-MX" -> "es"). Data files live in public/data/vocabulary-charsets
// so only the languages actually used get fetched, instead of bundling all of them.
//
// Japanese/Korean/Chinese use whole Unicode script blocks (kana, Han, Hangul syllables)
// rather than AWS's exact downloadable character lists, since those aren't available as
// text on the docs page - this is a deliberate approximation, slightly more permissive
// than AWS's real allowlist for those three languages.

function baseLanguageCode(languageCode) {
    return String(languageCode || '').split('-')[0].toLowerCase()
}

function buildAllowedSet(charset) {
    const set = new Set()

    for (const [start, end] of charset.ranges || []) {
        const from = parseInt(start, 16)
        const to = parseInt(end, 16)
        for (let cp = from; cp <= to; cp++) set.add(cp)
    }

    for (const hex of charset.codepoints || []) {
        set.add(parseInt(hex, 16))
    }

    return set
}

const charsetPromiseCache = new Map()

function fetchCharset(code) {
    if (!charsetPromiseCache.has(code)) {
        const promise = fetch(`/data/vocabulary-charsets/${code}.json`)
            .then(res => (res.ok ? res.json() : null))
            .then(charset => (charset ? buildAllowedSet(charset) : null))
            .catch(() => null)
        charsetPromiseCache.set(code, promise)
    }
    return charsetPromiseCache.get(code)
}

// Returns a Set of allowed Unicode code points for the given language code,
// or null if the language has no known charset (validation is skipped in that case).
export function getAllowedCharSet(languageCode) {
    return fetchCharset(baseLanguageCode(languageCode))
}

// Returns the distinct characters in `term` that aren't in `allowedSet`.
// A null allowedSet (unknown language) means "don't validate" -> no invalid chars.
export function getInvalidChars(term, allowedSet) {
    if (!allowedSet) return []

    const invalid = []
    for (const ch of String(term || '')) {
        if (ch === ' ') continue
        if (!allowedSet.has(ch.codePointAt(0)) && !invalid.includes(ch)) {
            invalid.push(ch)
        }
    }
    return invalid
}

export default { getAllowedCharSet, getInvalidChars }
